import * as React from 'react';
import { ImagePlus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  ROLE_OPTIONS,
  SITES_OPTIONS,
  VENUE_TYPES,
  formatNumber,
  type Answers,
  type Approver,
  type Errors,
  type Photo,
  type VenueType,
} from './config';
import { AddressInput } from './AddressInput';
import { MAX_PHOTOS, resizePhoto } from './photos';
import { Choice, MultiChoice, Question, inputClass } from './ui';

export interface StepProps {
  a: Answers;
  set: (patch: Partial<Answers>) => void;
  errors: Errors;
}

const textareaClass =
  'rounded-xl border-2 border-slate-200 bg-white text-base focus-visible:border-[#0A5CFF] focus-visible:ring-0 focus-visible:ring-offset-0';

const YES_CHECK_NO = [
  { value: 'yes' as const, label: 'Yes' },
  { value: 'check' as const, label: 'Need to check' },
  { value: 'no' as const, label: 'No' },
];
const YES_NO_UNSURE = [
  { value: 'yes' as const, label: 'Yes' },
  { value: 'no' as const, label: 'No' },
  { value: 'not_sure' as const, label: 'Not sure' },
];

export function VenueTypeStep({ a, onPick }: { a: Answers; onPick: (t: VenueType) => void }) {
  return (
    <div role="radiogroup" aria-label="Venue type" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {VENUE_TYPES.map(({ id, label, hint, icon: Icon }) => {
        const selected = a.venueType === id;
        const wide = id === 'other';
        return (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onPick(id)}
            className={cn(
              'flex items-center rounded-2xl border-2 transition-all active:scale-[0.98]',
              wide
                ? 'col-span-2 gap-4 p-4 text-left lg:col-span-4'
                : 'aspect-square flex-col justify-center gap-3 p-4 text-center lg:aspect-auto lg:px-3 lg:py-6',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A5CFF] focus-visible:ring-offset-2',
              selected ? 'border-[#0A5CFF] bg-[#0A5CFF]/[0.06]' : 'border-slate-200 bg-white hover:border-slate-300',
            )}
          >
            <span
              className={cn(
                'grid shrink-0 place-content-center rounded-full',
                wide ? 'h-12 w-12' : 'h-14 w-14',
                selected ? 'bg-[#0A5CFF] text-white' : 'bg-[#0A5CFF]/10 text-[#0A5CFF]',
              )}
            >
              <Icon className={wide ? 'h-6 w-6' : 'h-7 w-7'} />
            </span>
            <span className={cn(wide && 'flex flex-col gap-0.5')}>
              <span className="block text-base font-semibold leading-tight text-[#0A1628]">{label}</span>
              <span className={cn('text-xs text-slate-500', wide ? 'block' : 'mt-3 hidden sm:block')}>{hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function WhereStep({ a, set, errors }: StepProps) {
  const transit = a.venueType === 'transit';
  return (
    <>
      <Question label={transit ? 'Organisation / venue name' : 'Venue name'} htmlFor="venueName" error={errors.venueName}>
        <Input
          id="venueName"
          value={a.venueName}
          autoComplete="organization"
          placeholder={
            transit
              ? 'e.g. Miami International Airport'
              : a.venueType === 'retail'
                ? 'e.g. Westfield Century City'
                : a.venueType === 'other'
                  ? 'e.g. Grand Plaza Hotel'
                  : 'e.g. Downtown Fitness'
          }
          className={inputClass}
          onChange={(e) => set({ venueName: e.target.value })}
        />
      </Question>
      <Question
        label={transit ? 'Head office address' : 'Address'}
        hint="Use your head office address if you have multiple locations."
        htmlFor="address"
        error={errors.address}
      >
        <AddressInput
          id="address"
          value={a.address}
          onChange={(address, place) => set({ address, addressLat: place?.lat, addressLon: place?.lon })}
        />
      </Question>
      <Question label="Number of locations you operate" error={errors.sitesCount}>
        <Choice
          columns={2}
          value={a.sitesCount}
          onChange={(sitesCount) => set({ sitesCount })}
          options={SITES_OPTIONS.map((s) => ({ value: s, label: s }))}
        />
      </Question>
      {transit && (
        <Question label="Where could machines be located?" error={errors.securitySide}>
          <Choice
            columns={3}
            value={a.securitySide}
            onChange={(securitySide) => set({ securitySide })}
            options={[
              { value: 'landside', label: 'Landside' },
              { value: 'airside', label: 'Airside' },
              { value: 'both', label: 'Both' },
            ]}
          />
        </Question>
      )}
    </>
  );
}

function NumberInput({
  id,
  value,
  placeholder,
  maxDigits,
  onChange,
}: {
  id: string;
  value: string;
  placeholder: string;
  maxDigits: number;
  onChange: (value: string) => void;
}) {
  return (
    <Input
      id={id}
      inputMode="numeric"
      pattern="[0-9,]*"
      value={value}
      placeholder={placeholder}
      className={inputClass}
      onChange={(e) => {
        const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, maxDigits);
        onChange(digits ? formatNumber(parseInt(digits, 10)) : '');
      }}
    />
  );
}

const FOOTFALL_TOTAL: Partial<Record<VenueType, { label: string; placeholder: string }>> = {
  campus: { label: 'Total student enrollment', placeholder: 'e.g. 35,000' },
  transit: { label: 'Annual passenger numbers', placeholder: 'e.g. 12,000,000' },
  retail: { label: 'Annual footfall across all locations', placeholder: 'e.g. 8,000,000' },
  other: { label: 'Annual footfall across all locations', placeholder: 'e.g. 500,000' },
};

export function BusyStep({ a, set, errors }: StepProps) {
  const total = a.venueType && FOOTFALL_TOTAL[a.venueType];
  if (total) {
    return (
      <>
        <Question label={total.label} htmlFor="totalMembers" error={errors.totalMembers}>
          <NumberInput id="totalMembers" value={a.totalMembers} placeholder={total.placeholder} maxDigits={10} onChange={(totalMembers) => set({ totalMembers })} />
        </Question>
        <Question
          label="Average daily footfall at proposed location"
          htmlFor="dailyVisitors"
          note="Estimated number of people who pass through the area each day."
          error={errors.dailyVisitors}
        >
          <NumberInput id="dailyVisitors" value={a.dailyVisitors} placeholder="e.g. 4,500" maxDigits={8} onChange={(dailyVisitors) => set({ dailyVisitors })} />
        </Question>
      </>
    );
  }
  return (
    <>
      <Question label="Total active members across all locations" htmlFor="totalMembers" error={errors.totalMembers}>
        <NumberInput id="totalMembers" value={a.totalMembers} placeholder="e.g. 12,500" maxDigits={9} onChange={(totalMembers) => set({ totalMembers })} />
      </Question>
      <Question label="Average member visits per month (estate wide)" htmlFor="estateDailyVisitors" error={errors.estateDailyVisitors}>
        <NumberInput
          id="estateDailyVisitors"
          value={a.estateDailyVisitors}
          placeholder="e.g. 240,000"
          maxDigits={9}
          onChange={(estateDailyVisitors) => set({ estateDailyVisitors })}
        />
      </Question>
      <Question label="Average member visits per month (per club)" htmlFor="dailyVisitors" error={errors.dailyVisitors}>
        <NumberInput id="dailyVisitors" value={a.dailyVisitors} placeholder="e.g. 24,000" maxDigits={8} onChange={(dailyVisitors) => set({ dailyVisitors })} />
      </Question>
    </>
  );
}

interface SpotStepProps extends StepProps {
  photos: Photo[];
  setPhotos: (photos: Photo[]) => void;
}

export function SpotStep({ a, set, errors, photos, setPhotos }: SpotStepProps) {
  const fileInput = React.useRef<HTMLInputElement>(null);
  const [photoError, setPhotoError] = React.useState('');

  const addPhotos = async (files: FileList | null) => {
    if (!files?.length) return;
    setPhotoError('');
    const room = MAX_PHOTOS - photos.length;
    const picked = Array.from(files).filter((f) => f.type.startsWith('image/')).slice(0, room);
    try {
      const resized = await Promise.all(picked.map(resizePhoto));
      setPhotos([...photos, ...resized]);
    } catch (err) {
      setPhotoError(err instanceof Error ? err.message : 'Could not add that photo');
    }
    if (files.length > room) setPhotoError(`You can add up to ${MAX_PHOTOS} photos`);
  };

  return (
    <>
      <Question label="How many machines are you interested in?" hint="An estimate is fine." htmlFor="machineCount" error={errors.machineCount}>
        <NumberInput id="machineCount" value={a.machineCount} placeholder="e.g. 5" maxDigits={5} onChange={(machineCount) => set({ machineCount })} />
      </Question>
      <Question label="OK for machine sensors to count anonymized visitors?" error={errors.sensorsOk}>
        <Choice columns={3} value={a.sensorsOk} onChange={(sensorsOk) => set({ sensorsOk })} options={YES_CHECK_NO} />
      </Question>
      <Question label="Describe the location of the machines" htmlFor="machineLocation" error={errors.machineLocation}>
        <Textarea
          id="machineLocation"
          value={a.machineLocation}
          rows={2}
          maxLength={500}
          placeholder='e.g. "Library, floor 2, near the entrance"'
          className={textareaClass}
          onChange={(e) => set({ machineLocation: e.target.value })}
        />
      </Question>
      <Question label="Photos of potential machine location" hint={`Optional, up to ${MAX_PHOTOS}`} error={photoError || undefined}>
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative aspect-square overflow-hidden rounded-xl bg-slate-100">
              <img src={p.dataUrl} alt={p.name} className="h-full w-full object-cover" />
              <button
                type="button"
                aria-label={`Remove ${p.name}`}
                onClick={() => setPhotos(photos.filter((x) => x.id !== p.id))}
                className="absolute right-1 top-1 grid h-7 w-7 place-content-center rounded-full bg-black/60 text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 bg-white text-sm font-semibold text-[#0A5CFF] hover:border-[#0A5CFF]"
            >
              <ImagePlus className="h-6 w-6" />
              Add photo
            </button>
          )}
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            void addPhotos(e.target.files);
            e.target.value = '';
          }}
        />
      </Question>
    </>
  );
}

function RestrictionQuestion({
  label,
  value,
  details,
  error,
  placeholder,
  onChange,
  onDetails,
}: {
  label: string;
  value?: 'yes' | 'no';
  details: string;
  error?: string;
  placeholder: string;
  onChange: (value: 'yes' | 'no') => void;
  onDetails: (details: string) => void;
}) {
  return (
    <Question label={label} error={error}>
      <Choice
        value={value}
        onChange={onChange}
        options={[
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ]}
      />
      {value === 'yes' && (
        <Textarea
          aria-label="What are they?"
          value={details}
          rows={3}
          placeholder={placeholder}
          className={textareaClass}
          onChange={(e) => onDetails(e.target.value)}
        />
      )}
    </Question>
  );
}

export function ProductsStep({ a, set, errors }: StepProps) {
  return (
    <>
      <RestrictionQuestion
        label="Are there any restrictions on product sales?"
        value={a.productRestrictions}
        details={a.productLimits}
        error={errors.productRestrictions ?? errors.productLimits}
        placeholder="What are they? e.g. no energy drinks"
        onChange={(productRestrictions) => set({ productRestrictions })}
        onDetails={(productLimits) => set({ productLimits })}
      />
      <RestrictionQuestion
        label="Are there any restrictions on advertising?"
        value={a.adRestrictions}
        details={a.restrictedCategories}
        error={errors.adRestrictions ?? errors.restrictedCategories}
        placeholder="What are they? e.g. no alcohol or gambling ads"
        onChange={(adRestrictions) => set({ adRestrictions })}
        onDetails={(restrictedCategories) => set({ restrictedCategories })}
      />
      {a.venueType === 'retail' && (
        <Question label="Do any tenants have exclusive rights to sell drinks or snacks?" error={errors.tenantExclusivity}>
          <Choice
            columns={3}
            value={a.tenantExclusivity}
            onChange={(tenantExclusivity) => set({ tenantExclusivity })}
            options={YES_NO_UNSURE}
          />
        </Question>
      )}
    </>
  );
}

function TermQuestion({ a, set, errors }: StepProps) {
  return (
    <Question label="Select term duration" error={errors.termYears}>
      <Choice
        columns={3}
        value={a.termYears}
        onChange={(termYears) => set({ termYears })}
        options={[
          { value: '3', label: '3 years' },
          { value: '4', label: '4 years' },
          { value: '5', label: '5 years' },
        ]}
      />
    </Question>
  );
}

const APPROVER_OPTIONS: Partial<Record<VenueType, Array<{ value: Approver; label: string }>>> = {
  campus: [
    { value: 'estates_facilities', label: 'Estates / Facilities' },
    { value: 'procurement', label: 'Procurement' },
    { value: 'catering_retail', label: 'Catering / Retail' },
    { value: 'other', label: 'Other' },
  ],
  retail: [
    { value: 'center_management', label: 'Center management' },
    { value: 'landlord_owner', label: 'Landlord / Owner' },
    { value: 'leasing_team', label: 'Leasing team' },
    { value: 'other', label: 'Other' },
  ],
};

function ApproversBox({ a, set, errors }: StepProps) {
  const options = (a.venueType && APPROVER_OPTIONS[a.venueType]) || [];
  return (
    <div className="rounded-2xl bg-[#F4F6FA] p-4 sm:p-5">
      <Question label="Who needs to approve?" hint="Select all that apply." error={errors.approvers}>
        <MultiChoice
          label="Who needs to approve"
          columns={4}
          showCheck={false}
          value={a.approvers}
          onChange={(approvers) => set({ approvers })}
          options={options}
        />
      </Question>
    </div>
  );
}

function CampusAgreementStep({ a, set, errors }: StepProps) {
  return (
    <>
      <TermQuestion a={a} set={set} errors={errors} />
      <Question label="Who controls the proposed location?" error={errors.locationControl}>
        <Choice
          columns={3}
          value={a.locationControl}
          onChange={(locationControl) => set({ locationControl })}
          options={[
            { value: 'university', label: 'University owned' },
            { value: 'third_party', label: 'Third-party operated' },
            { value: 'not_sure', label: 'Not sure' },
          ]}
        />
      </Question>
      <Question label="Is additional approval required to install the machine?" error={errors.approvalNeeded}>
        <Choice
          columns={3}
          value={a.approvalNeeded}
          onChange={(approvalNeeded) => set({ approvalNeeded })}
          options={[
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes' },
            { value: 'not_sure', label: 'Not sure' },
          ]}
        />
      </Question>
      {a.approvalNeeded === 'yes' && <ApproversBox a={a} set={set} errors={errors} />}
    </>
  );
}

export function AgreementStep({ a, set, errors }: StepProps) {
  if (a.venueType === 'campus') return <CampusAgreementStep a={a} set={set} errors={errors} />;
  return (
    <>
      <TermQuestion a={a} set={set} errors={errors} />
      <Question label="Do you own or lease your locations?" error={errors.tenure}>
        <Choice
          columns={3}
          value={a.tenure}
          onChange={(tenure) => set({ tenure })}
          options={[
            { value: 'own', label: 'Own' },
            { value: 'lease', label: 'Lease' },
            { value: 'both', label: 'Both' },
          ]}
        />
      </Question>
      {(a.tenure === 'lease' || a.tenure === 'both') && (
        <div className="rounded-2xl bg-[#F4F6FA] p-4 sm:p-5">
          <Question
            label="Do your leases generally have 3+ years remaining?"
            hint="Select the best option for most of your locations."
            error={errors.leaseThreePlus}
          >
            <Choice
              columns={3}
              value={a.leaseThreePlus}
              onChange={(leaseThreePlus) => set({ leaseThreePlus })}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'varies', label: 'Varies' },
              ]}
            />
          </Question>
        </div>
      )}
      <Question label="Is third-party approval required?" error={errors.approvalNeeded}>
        <Choice columns={3} value={a.approvalNeeded} onChange={(approvalNeeded) => set({ approvalNeeded })} options={YES_NO_UNSURE} />
      </Question>
      {a.venueType === 'retail' && a.approvalNeeded === 'yes' && <ApproversBox a={a} set={set} errors={errors} />}
    </>
  );
}

export function ContactStep({ a, set, errors }: StepProps) {
  const field = (key: 'contactName' | 'email' | 'phone', props: React.ComponentProps<typeof Input>) => (
    <Input
      id={key}
      value={a[key]}
      className={inputClass}
      onChange={(e) => set({ [key]: e.target.value } as Partial<Answers>)}
      {...props}
    />
  );
  return (
    <>
      <Question label="Your name" htmlFor="contactName" error={errors.contactName}>
        {field('contactName', { autoComplete: 'name', placeholder: 'Full name' })}
      </Question>
      <Question label="Your role" error={errors.contactRole}>
        <Choice
          value={a.contactRole}
          onChange={(contactRole) => set({ contactRole })}
          options={ROLE_OPTIONS.map((r) => ({ value: r, label: r }))}
        />
      </Question>
      <Question label="Email" htmlFor="email" error={errors.email}>
        {field('email', { type: 'email', inputMode: 'email', autoComplete: 'email', placeholder: 'you@venue.com' })}
      </Question>
      <Question label="Phone" htmlFor="phone" error={errors.phone}>
        {field('phone', { type: 'tel', inputMode: 'tel', autoComplete: 'tel', placeholder: 'Best number to reach you' })}
      </Question>
      <Question label="Are you authorized to sign an agreement?" error={errors.canSign ?? errors.signerName ?? errors.signerEmail}>
        <Choice
          value={a.canSign}
          onChange={(canSign) => set({ canSign })}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'other', label: 'Someone else will sign' },
          ]}
        />
        {a.canSign === 'other' && (
          <div className="space-y-2">
            <Input
              aria-label="Signer name"
              value={a.signerName}
              placeholder="Their name"
              className={inputClass}
              onChange={(e) => set({ signerName: e.target.value })}
            />
            <Input
              aria-label="Signer email"
              type="email"
              inputMode="email"
              value={a.signerEmail}
              placeholder="Their email"
              className={inputClass}
              onChange={(e) => set({ signerEmail: e.target.value })}
            />
          </div>
        )}
      </Question>
      <Question
        label="Current income from unattended retail per month"
        hint="In dollars. Enter 0 if none."
        htmlFor="currentIncome"
        error={errors.currentIncome}
      >
        <NumberInput id="currentIncome" value={a.currentIncome} placeholder="e.g. 2,000" maxDigits={9} onChange={(currentIncome) => set({ currentIncome })} />
      </Question>
      <Question label="Reason for exploring connected retail" htmlFor="exploringReason" error={errors.exploringReason}>
        <Textarea
          id="exploringReason"
          value={a.exploringReason}
          rows={3}
          maxLength={1000}
          placeholder="e.g. We want 24/7 food and drink options for members"
          className={textareaClass}
          onChange={(e) => set({ exploringReason: e.target.value })}
        />
      </Question>
    </>
  );
}
